import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  NgZone
} from '@angular/core';
import * as THREE from 'three';

const vert = `
  attribute vec3 position;
  varying vec2 vUv;
  void main() {
    vUv = position.xy * 0.5 + 0.5;
    gl_Position = vec4(position, 1.0);
  }
`;

const frag = `
  precision highp float;
  uniform float iTime;
  uniform vec2 iResolution;
  uniform bool enableRainbow;
  uniform vec3 gridColor;
  uniform float rippleIntensity;
  uniform float gridSize;
  uniform float gridThickness;
  uniform float fadeDistance;
  uniform float vignetteStrength;
  uniform float glowIntensity;
  uniform float opacity;
  uniform float gridRotation;
  uniform bool mouseInteraction;
  uniform vec2 mousePosition;
  uniform float mouseInfluence;
  uniform float mouseInteractionRadius;
  varying vec2 vUv;

  float pi = 3.141592;

  mat2 rotate(float angle) {
    float s = sin(angle);
    float c = cos(angle);
    return mat2(c, -s, s, c);
  }

  void main() {
    vec2 uv = vUv * 2.0 - 1.0;
    uv.x *= iResolution.x / iResolution.y;

    if (gridRotation != 0.0) {
      uv = rotate(gridRotation * pi / 180.0) * uv;
    }

    float dist = length(uv);
    float func = sin(pi * (iTime - dist));
    vec2 rippleUv = uv + uv * func * rippleIntensity;

    if (mouseInteraction && mouseInfluence > 0.0) {
      vec2 mouseUv = (mousePosition * 2.0 - 1.0);
      mouseUv.x *= iResolution.x / iResolution.y;
      float mouseDist = length(uv - mouseUv);
      
      float influence = mouseInfluence * exp(-mouseDist * mouseDist / (mouseInteractionRadius * mouseInteractionRadius));
      
      float mouseWave = sin(pi * (iTime * 2.0 - mouseDist * 3.0)) * influence;
      rippleUv += normalize(uv - mouseUv) * mouseWave * rippleIntensity * 0.3;
    }

    vec2 a = sin(gridSize * 0.5 * pi * rippleUv - pi / 2.0);
    vec2 b = abs(a);

    float aaWidth = 0.5;
    vec2 smoothB = vec2(
      smoothstep(0.0, aaWidth, b.x),
      smoothstep(0.0, aaWidth, b.y)
    );

    vec3 color = vec3(0.0);
    color += exp(-gridThickness * smoothB.x * (0.8 + 0.5 * sin(pi * iTime)));
    color += exp(-gridThickness * smoothB.y);
    color += 0.5 * exp(-(gridThickness / 4.0) * sin(smoothB.x));
    color += 0.5 * exp(-(gridThickness / 3.0) * smoothB.y);

    if (glowIntensity > 0.0) {
      color += glowIntensity * exp(-gridThickness * 0.5 * smoothB.x);
      color += glowIntensity * exp(-gridThickness * 0.5 * smoothB.y);
    }

    float ddd = exp(-2.0 * clamp(pow(dist, fadeDistance), 0.0, 1.0));
    
    vec2 vignetteCoords = vUv - 0.5;
    float vignetteDistance = length(vignetteCoords);
    float vignette = 1.0 - pow(vignetteDistance * 2.0, vignetteStrength);
    vignette = clamp(vignette, 0.0, 1.0);
    
    vec3 t;
    if (enableRainbow) {
      t = vec3(
        uv.x * 0.5 + 0.5 * sin(iTime),
        uv.y * 0.5 + 0.5 * cos(iTime),
        pow(cos(iTime), 4.0)
      ) + 0.5;
    } else {
      t = gridColor;
    }

    float finalFade = ddd * vignette;
    float alpha = length(color) * finalFade * opacity;
    gl_FragColor = vec4(color * t * finalFade * opacity, alpha);
  }
`;

@Component({
  selector: 'nc-ripple-grid-background',
  templateUrl: './ripple-grid-background.component.html',
  styleUrls: ['./ripple-grid-background.component.less']
})
export class RippleGridBackgroundComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  /** 彩虹模式关闭时网格的颜色 */
  @Input() ncGridColor: string = '#5227ff';
  /** 控制中心波纹效应的强度（0-0.3）*/
  @Input() ncRippleIntensity: number = 0.05;
  /** 控制网格图案的密度/大小（5-30）*/
  @Input() ncGridSize: number = 10.0;
  /** 控制网格线的粗细（5-50）*/
  @Input() ncGridThickness: number = 15.0;
  /** 控制淡入淡出效果从中心向外延伸的距离（0.5-3）*/
  @Input() ncFadeDistance: number = 1.5;
  /** 控制暗角（边缘变暗）效果的强度（0.5-5）*/
  @Input() ncVignetteStrength: number = 2.0;
  /** 为网格线添加发光效果（0-1）*/
  @Input() ncGlowIntensity: number = 0.1;
  /** 整体效果的不透明度（0-1）*/
  @Input() ncOpacity: number = 1.0;
  /** 将整个网格图案旋转一定角度（0-360）*/
  @Input() ncGridRotation: number = 0;
  /** 启用鼠标/触摸交互以产生涟漪效 */
  private _mouseInteraction: boolean = true;
  @Input()
  set ncMouseInteraction(val: boolean | string) {
    this._mouseInteraction = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncMouseInteraction(): boolean {
    return this._mouseInteraction;
  }
  /** 控制鼠标交互效果的半径（0.2-2）*/
  @Input() ncMouseInteractionRadius: number = 1;
  /** 为网格启用彩虹色循环动画 */
  private _enableRainbow: boolean = false;
  @Input()
  set ncEnableRainbow(val: boolean | string) {
    this._enableRainbow = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncEnableRainbow(): boolean {
    return this._enableRainbow;
  }

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.OrthographicCamera;
  private material!: THREE.RawShaderMaterial;
  private mesh!: THREE.Mesh;

  private resizeObserver!: ResizeObserver;
  private intersectionObserver!: IntersectionObserver;
  private rafId: number | null = null;
  private resizeRafId: number | null = null;
  private isVisible: boolean = true;
  private isDestroyed: boolean = false;

  private clock = new THREE.Clock();

  private mousePosition = new THREE.Vector2(0.5, 0.5);
  private targetMouse = new THREE.Vector2(0.5, 0.5);
  private targetInfluence = 0;

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.initWebGL();
    this.setupResizeObserver();
    this.setupIntersectionObserver();
    this.ngZone.runOutsideAngular(() => {
      this.startAnimation();
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.cleanup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.material) return;
    this.updateUniforms();
  }

  private hexToRgb(hex: string): THREE.Vector3 {
    let cleanHex = hex;
    if (cleanHex.startsWith('#')) {
      cleanHex = cleanHex.slice(1);
    }
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
    return result
      ? new THREE.Vector3(
        parseInt(result[1], 16) / 255,
        parseInt(result[2], 16) / 255,
        parseInt(result[3], 16) / 255
      )
      : new THREE.Vector3(1, 1, 1);
  }

  private updateUniforms(): void {
    if (!this.material) return;
    const u = this.material.uniforms;
    u.enableRainbow.value = this.ncEnableRainbow;
    u.gridColor.value.copy(this.hexToRgb(this.ncGridColor));
    u.rippleIntensity.value = this.ncRippleIntensity;
    u.gridSize.value = this.ncGridSize;
    u.gridThickness.value = this.ncGridThickness;
    u.fadeDistance.value = this.ncFadeDistance;
    u.vignetteStrength.value = this.ncVignetteStrength;
    u.glowIntensity.value = this.ncGlowIntensity;
    u.opacity.value = this.ncOpacity;
    u.gridRotation.value = this.ncGridRotation;
    u.mouseInteraction.value = this.ncMouseInteraction;
    u.mouseInteractionRadius.value = this.ncMouseInteractionRadius;
  }

  private initWebGL(): void {
    const el = this.containerRef.nativeElement;

    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    const dpr = Math.min(window.devicePixelRatio, 2);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(el.offsetWidth, el.offsetHeight);
    el.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();

    // The camera doesn't matter much as we use RawShaderMaterial that sets gl_Position directly
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.camera.position.z = 1;

    const geometry = new THREE.PlaneGeometry(2, 2);

    this.material = new THREE.RawShaderMaterial({
      vertexShader: vert,
      fragmentShader: frag,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector2(el.offsetWidth, el.offsetHeight) },
        enableRainbow: { value: this.ncEnableRainbow },
        gridColor: { value: this.hexToRgb(this.ncGridColor) },
        rippleIntensity: { value: this.ncRippleIntensity },
        gridSize: { value: this.ncGridSize },
        gridThickness: { value: this.ncGridThickness },
        fadeDistance: { value: this.ncFadeDistance },
        vignetteStrength: { value: this.ncVignetteStrength },
        glowIntensity: { value: this.ncGlowIntensity },
        opacity: { value: this.ncOpacity },
        gridRotation: { value: this.ncGridRotation },
        mouseInteraction: { value: this.ncMouseInteraction },
        mousePosition: { value: new THREE.Vector2(0.5, 0.5) },
        mouseInfluence: { value: 0 },
        mouseInteractionRadius: { value: this.ncMouseInteractionRadius }
      },
      blending: THREE.NormalBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);

    this.clock.start();
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (!this.ncMouseInteraction || !this.containerRef) return;
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = 1.0 - (e.clientY - rect.top) / rect.height; // Flip Y coordinate
    this.targetMouse.set(x, y);
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.ncMouseInteraction) return;
    this.targetInfluence = 1.0;
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (!this.ncMouseInteraction) return;
    this.targetInfluence = 0.0;
  }

  private setupResizeObserver(): void {
    const el = this.containerRef.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);
      this.ngZone.runOutsideAngular(() => {
        this.resizeRafId = requestAnimationFrame(() => {
          if (!this.renderer || !el) return;
          const w = el.offsetWidth || 1;
          const h = el.offsetHeight || 1;
          this.renderer.setSize(w, h);
          if (this.material) {
            this.material.uniforms.iResolution.value.set(w, h);
          }
          this.resizeRafId = null;
        });
      });
    });
    this.resizeObserver.observe(el);
  }

  private setupIntersectionObserver(): void {
    const el = this.containerRef.nativeElement;
    this.intersectionObserver = new IntersectionObserver((entries) => {
      this.isVisible = entries[0].isIntersecting;
      if (this.isVisible) {
        if (this.rafId === null) {
          this.ngZone.runOutsideAngular(() => {
            this.startAnimation();
          });
        }
      }
    }, { threshold: [0, 0.01, 0.1] });
    this.intersectionObserver.observe(el);
  }

  private startAnimation(): void {
    if (this.isDestroyed) return;
    const render = () => {
      if (!this.isVisible) {
        this.rafId = null;
        return;
      }
      this.rafId = requestAnimationFrame(render);

      const t = this.clock.getElapsedTime();

      if (this.material) {
        this.material.uniforms.iTime.value = t;

        // Mouse smoothing
        const lerpFactor = 0.1;
        this.mousePosition.x += (this.targetMouse.x - this.mousePosition.x) * lerpFactor;
        this.mousePosition.y += (this.targetMouse.y - this.mousePosition.y) * lerpFactor;

        const currentInfluence = this.material.uniforms.mouseInfluence.value;
        this.material.uniforms.mouseInfluence.value += (this.targetInfluence - currentInfluence) * 0.05;

        this.material.uniforms.mousePosition.value.copy(this.mousePosition);
      }

      this.renderer.render(this.scene, this.camera);
    };
    render();
  }

  private cleanup(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.resizeRafId !== null) cancelAnimationFrame(this.resizeRafId);

    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();

    if (this.renderer) {
      const el = this.containerRef?.nativeElement;
      if (el && this.renderer.domElement.parentNode === el) {
        el.removeChild(this.renderer.domElement);
      }
      this.renderer.dispose();
    }
  }
}
